import React, { useRef } from 'react';
import { Field, FieldValidatorType } from '@progress/kendo-react-form';
import { formatDate } from '@progress/kendo-intl';
import { DataType } from '../../../config/Constants';
import { convertDateFormat, toRadioData, toRadioEnum } from '../../helpers/Utils';
import {
  FormDatePicker,
  FormTimePicker,
  FormDateTimePicker,
  FormDateRangePicker,
  FormNumericTextBox,
  FormInput,
  FormColorPicker,
  FormSwitch,
  FormCheckbox,
  FormMaskedTextBox,
  FormDropDownList,
  FormAutoComplete,
  FormSlider,
  FormUpload,
  FormComboBox,
  FormMultiSelect,
  FormMultiSelectAll,
  FormRadioGroup,
  FormGuidMultiSelect,
  FormTextArea,
} from '../panels/FormComponents';
import {
  emailValidator,
  nameValidator,
  checkboxValidator,
  emailRequiredValidator,
  dateValidator,
  colorValidator,
  isoValidator,
  userNameValidator,
  guidValidator,
  radioGroupValidator,
  guidOptionalValidator,
  guidListValidator,
  idDisplayValidator,
  cultureValidator,
  idDisplayListValidator,
  enumListValidator,
  enumValidator,
  orderValidator,
  requiredValueValidator
} from '../../helpers/Validators';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { CommonResources } from '../../../config/CommonResources';
import { IEnumProps, EnumType, EntityStatusEnum } from '../../../config/Enums';
import { IFieldDataSourceProps, IQueryProps, IWhereProps } from '../../services/GraphQLShared';
import { fetchPagedList, getPowerBIGroupList } from '../../../services/ApiServices';
import { filterBy } from '@progress/kendo-data-query';
import { useInternationalization } from '@progress/kendo-react-intl';
import { TemplateType } from './IconTemplate';
import { OnDemandIcon } from '../../helpers/IconLoader';
import { Col, Row } from 'react-bootstrap';

export interface IFieldBoxBaseProps {
  field: string;
  title?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  sticky?: boolean;
  placeholder?: string;
  onChange?: (event: any) => void;
}
export interface IFieldBoxProps extends IFieldBoxBaseProps {
  type?: DataType;
  format?: string;
  mask?: string;
  dataSource?: IQueryProps;
  icon?: string | IconProp;
  route?: string;
  enum?: EnumType;
  template?: TemplateType;
  style?: any;
  validator?: FieldValidatorType; // overrides the validator picked by type/field name below
}
export function FieldBox(props: IFieldBoxProps): React.ReactElement | null {
  const getComponent = () => {
    switch (props.type) {
      case DataType.Date:
        return FormDatePicker;
      case DataType.DateTime:
        return FormDateTimePicker;
      case DataType.DateRange:
        return FormDateRangePicker;
      case DataType.Integer:
        return FormNumericTextBox;
      case DataType.Decimal:
        return FormNumericTextBox;
      case DataType.Currency:
        return FormNumericTextBox;
      case DataType.CheckBox:
        return FormCheckbox;
      case DataType.Switch:
        return FormSwitch;
      case DataType.Color:
        return FormColorPicker;
      case DataType.RadioButton:
        return FormRadioGroup;
      case DataType.AutoComplete:
        return FormAutoComplete;
      case DataType.DropDown:
        return FormDropDownList;
      case DataType.ComboBox:
        return FormComboBox;
      case DataType.MultiSelect:
        return FormMultiSelect;
      case DataType.MultiSelectAll:
        return FormMultiSelectAll;
      case DataType.Slider:
        return FormSlider;
      case DataType.FileUpload:
        return FormUpload;
      case DataType.MaskedText:
        return FormMaskedTextBox;
      case DataType.Time:
        return FormTimePicker;
      case DataType.AreaText:
        return FormTextArea;
      case DataType.RadioGroup:
        return FormRadioGroup;
      case DataType.List:
        return FormGuidMultiSelect;
      case DataType.Input:
        return FormInput;
      default:
        return FormInput;
    }
  };
  const getValidator = () => {
    switch (props.type) {
      case DataType.Date:
        return props.required === true ? dateValidator : undefined;
      case DataType.CheckBox:
        return checkboxValidator;
      case DataType.Color:
        return colorValidator;
      case DataType.RadioGroup:
        return radioGroupValidator;
      case DataType.Guid:
        return guidValidator;
      case DataType.List:
        return guidListValidator;
      case DataType.Email:
        return props.required === true ? emailRequiredValidator : emailValidator;
      case DataType.ComboBox:
        return props.required === true ? idDisplayValidator : undefined;
      default:
        if (props.disabled === true) return undefined;
        if (props.field === 'name') return nameValidator;
        if (props.field === 'userName') return userNameValidator;
        if (props.field === 'isoCode') return isoValidator;
        if (props.field === 'culture') return cultureValidator;
        if (props.field === 'email' || props.field.endsWith('.email'))
          return props.required === true ? emailRequiredValidator : emailValidator;
        if (props.field === 'upn' || props.field.endsWith('.upn')) return props.required === true ? emailRequiredValidator : undefined;
        if (props.field === 'connectionString') return props.required === true ? guidValidator : guidOptionalValidator;
        if (props.field === 'secureId') return props.required === true ? guidValidator : guidOptionalValidator;
        if (props.field === 'order') return orderValidator;
        if (props.field === 'comment') return props.required === true ? requiredValueValidator : undefined;
        return undefined;
    }
  };
  return (
    <Field
      id={props.field}
      name={props.field}
      title={props.title}
      mask={props.mask}
      component={getComponent()}
      validator={props.validator ?? getValidator()}
      hint={props.hint}
      disabled={props.disabled}
      placeholder={props.placeholder}
      format={props.format}
      optional={props.required === false}
      required={props.required === true}
      readOnly={props.disabled === true || props.sticky === true}
      onChange={props.onChange}
      style={props.style}
    />
  );
}

export interface IFieldDataBoxProps extends IFieldBoxBaseProps {
  type?: DataType;
  initialValue?: any;
  route?: string;
  variables?: any;
  dataSource: IFieldDataSourceProps;
  mutateData?: any;
  filters?: IWhereProps[];
  cascade?: ICascadeFilterProps[];
  itemRender?: any;
  valueRender?: any;
  mode?: 'direct' | 'where' | undefined;
  remote?: boolean;
  isNot?: boolean;
}
export interface ICascadeFilterProps {
  from: string;
  to: string;
  value?: any;
  values?: any;
}
export function FieldDataBox(props: IFieldDataBoxProps): React.ReactElement | null {
  const fetchImpl = (variables: any): Promise<any[]> => {
    if (props.dataSource.restApiEndPoint) {
      // Same paged REST endpoint the entity's grid already uses - request a large page and
      // unwrap .value to a flat list instead of the grid's edges/totalCount shape. Spread
      // `variables` first (not just search/entityStatus/where) so extra scoping keys from
      // `props.variables` (e.g. `variables={{reportId}}`) reach fetchPagedList instead of being
      // dropped - otherwise the option list comes back unscoped across every report.
      return fetchPagedList<any>(props.dataSource.restApiEndPoint, {
        ...variables,
        first: 1000,
        after: '0',
      }).then((page) => page.value);
    }
    return getPowerBIGroupList<any>(props.dataSource.rest!.url(variables), props.dataSource.rest!.params(variables));
  };
  const [data, setData] = React.useState({
    subsetData: [],
    skip: 0,
    total: 0,
  });
  const loaded = React.useRef(false);
  const allData = React.useRef([]);
  const filteredData = React.useRef(allData.current.slice());
  const pageSize = 20;
  const getComponent = () => {
    switch (props.type) {
      case DataType.AutoComplete:
        return FormAutoComplete;
      case DataType.DropDown:
        return FormDropDownList;
      case DataType.ComboBox:
        return FormComboBox;
      case DataType.MultiSelect:
        return FormMultiSelect;
      case DataType.MultiSelectAll:
        return FormMultiSelectAll;
      case DataType.Slider:
        return FormSlider;
      case DataType.RadioGroup:
        return FormRadioGroup;
      default:
        return FormInput;
    }
  };
  const getFilter = (filter: string) => {
    let variables: any = {};
    if (props.variables) variables = props.variables;
    variables['where'] = props.filters;
    variables['entityStatus'] = EntityStatusEnum.Active;
    variables['search'] = filter;
    return variables;
  };

  // Shared by both transports and both onOpen/onFilterChange: applies mutateData and pushes
  // the fetched list into state, regardless of which transport produced it.
  const applyRoot = (root: any[]) => {
    const tmpData = props.mutateData ? props.mutateData(root) : root;
    allData.current = tmpData;
    filteredData.current = tmpData.slice();
    setLoading(false);
    setData({ ...data, subsetData: tmpData.slice(0, pageSize), total: tmpData.length });
  };

  const fetchList = (filterValue: string) => {
    const variables = getFilter(filterValue);
    fetchImpl(variables)
      .then(applyRoot)
      .catch(() => setLoading(false));
  };

  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  function onFilterChange(event: any) {
    setLoading(true);
    if (props.remote) {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    }
    else {
      filteredData.current = filterBy(allData.current.slice(), event.filter);
      setLoading(false);
      setData({
        subsetData: filteredData.current.slice(0, pageSize),
        skip: 0,
        total: filteredData.current.length,
      });
    }
    debounceTimeout.current = setTimeout(() => fetchList(event.filter.value), 1000); // 1 second debounce
  }

  const pageChange = (event: any) => {
    const skip = event.page.skip;
    const take = event.page.take;
    setData({
      ...data,
      subsetData: filteredData.current.slice(skip, skip + take),
      skip: skip,
    });
  };

  const onOpen = () => {
    if (!loaded.current) {
      setLoading(true);
      loaded.current = true;
      fetchList('');
    }
  };

  const [loading, setLoading] = React.useState(false);
  const listDataRender = (element: React.ReactElement<HTMLDivElement>) => {
    const loadingData = props.remote ? (
      <h4>
        Type at least 3 characters to search
      </h4>
    ) : (
      <h4>
        <OnDemandIcon icon={'spinner'} size="lg" spin fixedWidth={true} style={{ marginRight: '5px' }} />Loading Data
      </h4>
    );

    const noData = (
      <h4>
        No data found
      </h4>
    );
    const content = loading ? loadingData : (data.subsetData.length === 0 ? noData : element.props);

    return React.cloneElement(element, { ...element.props }, content as React.ReactNode);
  };

  if (props.sticky === true)
    return (
      <Field
        {...props}
        id={props.field}
        name={props.field}
        component={FormComboBox}
        label={props.title}
        placeholder={props.placeholder}
        hint={props.hint}
        textField={props.dataSource.textField}
        dataItemKey={props.dataSource.keyField}
        required={props.required === true}
        disabled={true}
        readOnly={true}
      />
    );

  return (
    <Field
      {...props}
      id={props.field}
      name={props.field}
      textField={props.dataSource.textField}
      dataItemKey={props.dataSource.keyField}
      data={props.type == DataType.RadioGroup ? toRadioData(data.subsetData) : data.subsetData}
      validator={props.required ? (props.type == DataType.MultiSelect || props.type == DataType.MultiSelectAll ? idDisplayListValidator : idDisplayValidator) : undefined}
      component={getComponent()}
      label={props.title}
      placeholder={props.placeholder ?? `Select a ${props.title}`}
      virtual={{
        total: data.total,
        pageSize: pageSize,
        skip: data.skip,
      }}
      listNoDataRender={listDataRender}
      onPageChange={pageChange}
      onOpen={onOpen}
      filterable={true}
      onFilterChange={onFilterChange}
      hint={props.hint}
      optional={props.required === false}
      required={props.required === true}
      readOnly={props.disabled === true}
      disabled={props.disabled}
      isNot={props.isNot}
    />
  );
}

export interface IFieldEnumBoxProps extends IFieldBoxBaseProps {
  data: any;
  type?: DataType.DropDown | DataType.MultiSelect | DataType.MultiSelectAll | DataType.RadioGroup;
}
export function FieldEnumBox(props: IFieldEnumBoxProps): React.ReactElement | null {
  const renderTemplate = (dataItem: IEnumProps) => {
    return (
      <>
        <OnDemandIcon icon={dataItem.icon} color={dataItem.color} fixedWidth />
        <span className={'ms-1'} style={{ fontWeight: 300 }}>
          {dataItem.text}
        </span>
      </>
    );
  };
  const renderItem = (element: any, itemProps: any) => React.cloneElement(element, element.props, renderTemplate(itemProps.dataItem));
  const renderValue = (element: any, value: any) => {
    if (!value) return element;
    return React.cloneElement(element, { ...element.props }, renderTemplate(value));
  };
  if (props.type == DataType.RadioGroup)
    return (
      <Field
        {...props}
        data={toRadioEnum(props.data)}
        id={props.field}
        name={props.field}
        textField={'text'}
        dataItemKey={'code'}
        component={FormRadioGroup}
        label={props.title}
        hint={props.hint}
        layout={'horizontal'}
        validator={radioGroupValidator}
        required={props.required === true}
        optional={props.required === false}
        readOnly={props.disabled === true || props.sticky === true}
      />
    );
  return (
    <Field
      {...props}
      id={props.field}
      name={props.field}
      textField={'text'}
      dataItemKey={'code'}
      component={props.type == DataType.MultiSelect || props.type == DataType.MultiSelectAll ? FormMultiSelect : FormDropDownList}
      itemRender={renderItem}
      valueRender={renderValue}
      validator={props.required ? (props.type == DataType.MultiSelect || props.type == DataType.MultiSelectAll ? enumListValidator : enumValidator) : undefined}
      label={props.title}
      hint={props.hint}
      required={props.required === true}
      optional={props.required === false}
      readOnly={props.disabled === true || props.sticky === true}
    />
  );
}

export interface IAuditBoxProps {
  modifiedBy: string;
  modifiedOn: string;
  createdBy: string;
  createdOn: string;
  sourceCreatedDate?: string;
  sourceDeletedDate?: string;
}
export function AuditBox(props: IAuditBoxProps): React.ReactElement {
  const intl = useInternationalization();
  const dateCreated: Date = convertDateFormat(props.createdOn ? new Date(props.createdOn) : null, intl);
  const dateModified: Date = convertDateFormat(props.modifiedOn ? new Date(props.modifiedOn) : null, intl);
  const sourceDeletedDate: Date = convertDateFormat(props.sourceDeletedDate ? new Date(props.sourceDeletedDate) : null, intl);
  const sourceCreatedDate: Date = convertDateFormat(props.sourceCreatedDate ? new Date(props.sourceCreatedDate) : null, intl);
  const AuditTag = (props: { title: string; author?: string; date?: Date; icon?: IconProp; color?: string }): React.ReactElement | null => {
    if (!props.date) return null;
    const display = props.author ? `${props.author} on ${formatDate(props.date, 'F')}` : formatDate(new Date(props.date), 'F');
    return (
      <p className="mb-0">
        <>
          {props.icon && <OnDemandIcon icon={props.icon} color={props.color} />}
          {props.title}
          {display}
        </>
      </p>
    );
  };
  return (
    <Row>
      <Col className="my-1">
        <AuditTag title={CommonResources.ModifiedBy} author={props.modifiedBy} date={dateModified} />
        <AuditTag title={CommonResources.CreatedBy} author={props.createdBy} date={dateCreated} />
        <AuditTag title={CommonResources.SourceCreatedDate} date={sourceCreatedDate} icon={'user-plus'} color="#0080c8" />
        <AuditTag title={CommonResources.SourceDeletedDate} date={sourceDeletedDate} icon={'user-times'} color="#f34723" />
      </Col>
    </Row>
  );
}
